import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { fetchReprocessingInvoices } from './fetchReprocessingInvoices';
import {
  type GetReprocessingInvoicesPayload,
  type ReprocessingInvoicesResponse,
} from './types';

/**
 * Get the query options for fetching reprocessing invoices.
 * @param options - Optional configuration for the query.
 * @param queryParameters - The search parameters for fetching the invoices.
 * @returns The query options.
 */
export const getReprocessingInvoicesQueryOptions = ({
  options = {},
  queryParameters,
}: {
  options?: Omit<
    UseQueryOptions<ReprocessingInvoicesResponse>,
    'queryKey' | 'queryFn'
  >;
  queryParameters: Partial<GetReprocessingInvoicesPayload>;
}): UseQueryOptions<ReprocessingInvoicesResponse> => {
  return {
    queryFn: async () => fetchReprocessingInvoices(queryParameters),
    queryKey: queryKeysInvoiceReprocessing.getAll(queryParameters),
    staleTime: ms('30s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the data for all invoices requiring reprocessing.
 * Handles pagination and prefetches the next page of invoice processes for better performance.
 * @param queryParameters - The search parameters for fetching the invoices.
 * @param options - Optional configuration for the query.
 * @returns The query result containing the reprocessing invoices data.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_invoice_processes_invoice_process_get
 */
export const useQueryReprocessingInvoices = (
  queryParameters: Partial<GetReprocessingInvoicesPayload>,
  options?: Omit<
    UseQueryOptions<ReprocessingInvoicesResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryClient = useQueryClient();

  const nextPageOffset =
    (queryParameters.offset ?? 0) +
    (queryParameters.limit ?? PAGINATION_PAGE_SIZE_DEFAULT);

  void queryClient.prefetchQuery(
    getReprocessingInvoicesQueryOptions({
      options,
      queryParameters: {
        ...queryParameters,
        offset: nextPageOffset,
      },
    }),
  );

  return useQuery<ReprocessingInvoicesResponse>({
    ...getReprocessingInvoicesQueryOptions({
      options,
      queryParameters,
    }),
    placeholderData: keepPreviousData,
  });
};
